class HotspotInteractive extends HTMLElement {
  constructor() {
    super();
    this.activeId = null;
    this.isMobile = window.matchMedia("(max-width: 1023px)").matches;
    this.ignoreScroll = false;
  }

  connectedCallback() {
    requestAnimationFrame(() => this.setup());
  }

  setup() {
    this.baseImage = this.querySelector("[data-base]");
    this.xrayImages = this.querySelectorAll("[data-xray]");
    this.hotspots = this.querySelectorAll("[data-hotspot]");
    this.panels = this.querySelectorAll("[data-panel]");
    this.infoArea = this.querySelector(".hotspot-info-area");

    if (this.infoArea) this.infoArea.style.maxHeight = "0px";
    if (!this.hotspots.length) return;

    this.baseImage?.classList.add("visible");

    // Touch devices emit phantom mouseenter/mouseleave around a tap, which
    // races the click toggle and leaves the panel stuck open. Bind hover only
    // on devices that actually have a hover-capable pointer (desktop).
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    this.hotspots.forEach(h => {
      const id = h.dataset.hotspot;

      h.addEventListener("click", () => this.toggle(id));

      if (canHover) {
        // Gate by a LIVE breakpoint check too so a resize can't enable hover
        // at a mobile width.
        h.addEventListener("mouseenter", () => {
          if (!this.matchesMobile()) this.open(id, true);
        });
        h.addEventListener("mouseleave", () => {
          if (!this.matchesMobile()) this.restoreActive(true);
        });
      }
    });

    // No scroll-to-close: scrolling to read the open box must NOT close it.
    // The panel closes only when the user taps the dot's minus toggle.
  }

  matchesMobile() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }

  toggle(id) {
    if (this.activeId === id) {
      this.closeAll();
      return;
    }
    this.open(id, false);
  }

  /* =============================
     🔥 SINGLE SOURCE OF TRUTH
     Info box active → x-ray visible
     ============================= */
  open(id, previewMode = false) {
    const hotspot = this.querySelector(`[data-hotspot="${id}"]`);
    const panel = this.querySelector(`[data-panel="${id}"]`);
    const xray = this.querySelector(`[data-xray="${id}"]`);

    if (!hotspot || !panel) return;

    this.resetState();

    hotspot.classList.add("active");
    panel.classList.add("active");

    // 🔥 Show X-ray ALWAYS when panel opens
    this.showXray(xray);

    if (this.infoArea) {
      this.infoArea.style.maxHeight = panel.scrollHeight + "px";
    }

    if (!previewMode) {
      this.activeId = id;

      if (this.matchesMobile()) {
        panel.classList.add("mobile-open");
        setTimeout(() => {
          const rect = this.infoArea.getBoundingClientRect();
          const panelBottom = window.pageYOffset + rect.bottom;
          const viewportBottom = window.pageYOffset + window.innerHeight;
          if (panelBottom > viewportBottom) {
            const scrollTop = window.pageYOffset + rect.bottom - window.innerHeight + 20;
            window.scrollTo({ top: scrollTop, behavior: "smooth" });
          }
        }, 320);
      }
    }
  }

  /* =============================
     🔥 Restore based on open panel
     ============================= */
  restoreActive(isPreview = false) {
    this.resetState();

    if (this.activeId) {
      const hotspot = this.querySelector(
        `[data-hotspot="${this.activeId}"]`
      );
      const panel = this.querySelector(
        `[data-panel="${this.activeId}"]`
      );
      const xray = this.querySelector(
        `[data-xray="${this.activeId}"]`
      );

      hotspot?.classList.add("active");
      panel?.classList.add("active");

      // 🔥 Info box open → show its X-ray
      this.showXray(xray);

      if (this.infoArea && panel) {
        this.infoArea.style.maxHeight = panel.scrollHeight + "px";
      }
    } else {
      // 🔥 No info box → base image
      this.showBase();
    }
  }

  closeAll() {
    this.activeId = null;

    if (this.infoArea) this.infoArea.style.maxHeight = "0px";

    this.showBase();

    setTimeout(() => {
      this.hotspots.forEach(h => h.classList.remove("active"));
      this.panels.forEach(p =>
        p.classList.remove("active", "mobile-open")
      );
    }, 300);
  }

  /* ---------- HELPERS ---------- */

  resetState() {
    this.hotspots.forEach(h => h.classList.remove("active"));
    this.panels.forEach(p =>
      p.classList.remove("active", "mobile-open")
    );
  }

  showXray(activeXray) {
    this.baseImage?.classList.remove("visible");

    this.xrayImages.forEach(img =>
      img.classList.remove("visible")
    );

    activeXray?.classList.add("visible");
  }

  showBase() {
    this.baseImage?.classList.add("visible");

    this.xrayImages.forEach(img =>
      img.classList.remove("visible")
    );
  }
}

customElements.define(
  "hotspot-interactive",
  HotspotInteractive
);
